import { useState, useEffect } from 'react';
import { useLandbotContext } from 'context/landbot.context';
import { Subject, of } from 'rxjs';
import { delayWhen, concatMap, skip, take, filter } from 'rxjs/operators';
import EVENTS from 'constants/EVENTS';

const next$ = new Subject();
const next = () => next$.next();

export default function useMessagePipeline(mode = 'sequential') {
  const { landbot } = useLandbotContext();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState({
    key: null,
    message: '...',
    isPlaceholder: true,
  });
  const [end, setEnd] = useState(false);
  const [sequenceKey, setSequenceKey] = useState(0);
  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    setMessages([]);

    function onMessage(message) {
      if (message.type === 'hidden') {
        if (message.action === 'script') {
          eval(message.script);
        }
        if (message.action === 'finish') {
          setEnd(true);
        }
      } else if (message) {
        setMessage(withVariablesReplaced(message));
        setMessages((state) => [...state, message]);
      }
    }

    landbot.events.once('send_message', () => {
      setSequenceKey((state) => state + 1);
    });

    if (mode === 'sequential') {
      // MODE: SEQUENTIAL
      landbot.pipelines.$readableSequence
        .pipe(filter(messagesFilter), take(1))
        .subscribe(onMessage);

      const subscription = landbot.pipelines.$readableSequence
        .pipe(filter(messagesFilter), skip(1))
        .subscribe(onMessage);

      return () => subscription.unsubscribe();
    } else if (mode === 'click') {
      // MODE: CLICK
      landbot.pipelines.$sequence
        .pipe(filter(messagesFilter), take(1))
        .subscribe(onMessage);

      const subscription = landbot.pipelines.$sequence
        .pipe(
          filter(messagesFilter),
          skip(1),
          concatMap((message) => {
            if (message.type === 'hidden') {
              return of(message);
            }
            setEnd(false);
            return of(message).pipe(delayWhen(() => next$));
          })
        )
        .subscribe(onMessage);

      return () => subscription.unsubscribe();
    }
  }, [landbot, sequenceKey]);

  useEffect(() => {
    const onSetSpeaker = (object) => {
      if (!object?.onlyAvatar) {
        setMessage({
          key: null,
          message: '...',
          isPlaceholder: true,
        });
      }
    };
    landbot.events.on(EVENTS.SET_SPEAKER, onSetSpeaker);
    return () => landbot.events.off(EVENTS.SET_SPEAKER, onSetSpeaker);
  }, [landbot]);

  return { message, messages, next, end, canContinue, setCanContinue };
}

function messagesFilter(message) {
  return (
    ['text', 'dialog', 'hidden'].includes(message.type) &&
    message.samurai !== undefined
  );
}

function withVariablesReplaced(message) {
  /** customData is an object containing variables. */
  const rich_text = transformVariables(message.rich_text, window.__variables);
  return {
    ...message,
    rich_text,
  };
}

function transformVariables(str, variables) {
  if (!str) {
    return '';
  }

  let resultStr = str;

  const fieldDetectorRegex = /¿·([^·?]*)·\?/g;
  const fieldRegex = /(?:^@|[^\w\d@]@)(\w+)/g;
  const extendedFieldRegex = /@{([^}]*)}/g;

  let match = null;
  match = fieldDetectorRegex.exec(str);
  while (match) {
    resultStr = resultStr.replace(`¿·${match[1]}·?`, match[1]);
    match = fieldDetectorRegex.exec(str);
  }

  match = null;
  match = fieldRegex.exec(str);
  while (match) {
    resultStr = resultStr.replace(`@${match[1]}`, variables[match[1]] || '');
    match = fieldRegex.exec(str);
  }

  match = null;
  match = extendedFieldRegex.exec(str);
  while (match) {
    resultStr = resultStr.replace(
      `@{${match[1]}}`,
      variables[match[1]] || '',
    );
    match = extendedFieldRegex.exec(str);
  }

  return resultStr;
}